import { h, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { Button, Modal, Table } from 'ant-design-vue';
import type { AppError, ComponentDataImportResult } from 'shared';

/** 宿主统一提示导入失败和额度不足，成功结果由组件自行反馈。 */
export function useComponentDataFeedback() {
  const router = useRouter();
  const batches = new Set<string>();
  const dialogs = new Set<ReturnType<typeof Modal.warning>>();
  let quotaDialog: ReturnType<typeof Modal.warning> | undefined;
  const icon = () => h('i', { class: 'ri-error-warning-line', 'aria-hidden': 'true' });
  const number = (value: number) => value.toLocaleString('zh-CN');

  function open(options: Parameters<typeof Modal.warning>[0]) {
    const dialog = Modal.warning({
      ...options,
      icon: icon(),
      afterClose: () => {
        dialogs.delete(dialog);
        if (quotaDialog === dialog) quotaDialog = undefined;
        options.afterClose?.();
      },
    });
    dialogs.add(dialog);
    return dialog;
  }

  function manageQuota(canManage = true, beforeNavigate?: () => void) {
    if (!canManage || !router.hasRoute('account-orders'))
      return h('p', '请在工作台的“套餐、用量与订单”中管理额度；协作者请联系工作台所有者。');
    return h(
      Button,
      {
        onClick: () => {
          beforeNavigate?.();
          void router.push({ name: 'account-orders' });
        },
      },
      () => '管理额度',
    );
  }

  function confirmPartial(preview: ComponentDataImportResult): Promise<boolean> {
    const quota = preview.quota!;
    return new Promise((resolve) => {
      const content = h('div', [
        h(
          'p',
          `本次预计新增 ${number(preview.created)} 条、更新 ${number(preview.updated)} 条记录。数据所属账户已使用 ${number(quota.used)} / ${number(quota.limit)} 条，剩余 ${number(quota.remaining)} 条可用额度，无法全部导入。`,
        ),
        h(
          'p',
          '继续后仅保存能够成功写入的记录，其余行会列入未导入明细。更新已有记录不占新增额度，实际结果以写入时的额度为准。',
        ),
        manageQuota(quota.canManage, () => {
          resolve(false);
          dialog.destroy();
        }),
      ]);
      // 没有任何可写入的行时只提示，不提供无效的继续操作。
      const canContinue = quota.remaining > 0 || preview.updated > 0;
      const dialog = canContinue
        ? Modal.confirm({
            title: '数据库记录额度不足',
            icon: icon(),
            content,
            okText: '继续导入可处理的记录',
            cancelText: '取消',
            autoFocusButton: 'cancel',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
            afterClose: () => {
              dialogs.delete(dialog);
              resolve(false);
            },
          })
        : open({
            title: '数据库记录额度不足',
            content,
            okText: '知道了',
            afterClose: () => resolve(false),
          });
      dialogs.add(dialog);
    });
  }

  function showResult(result: ComponentDataImportResult, canManage: boolean) {
    if (result.dryRun || !result.failed || batches.has(result.batchId)) return;
    batches.add(result.batchId);
    const saved = result.created + result.updated;
    const failed = result.rows.filter((row) => row.status === 'failed');
    const quotaFailed = failed.filter((row) => row.code === 'QUOTA_EXCEEDED').length;
    open({
      title: saved ? '部分导入完成' : '导入未完成',
      okText: '知道了',
      width: 640,
      content: h('div', [
        h(
          'p',
          `新增 ${number(result.created)} 条，更新 ${number(result.updated)} 条，跳过 ${number(result.skipped)} 条，未导入 ${number(result.failed)} 条。`,
        ),
        h(
          'p',
          `${saved ? '已成功写入的数据已保存。' : ''}${quotaFailed ? `其中 ${number(quotaFailed)} 条因额度不足未导入。` : '请根据逐行原因修正数据后，仅重新导入未成功的记录。'}`,
        ),
        ...(quotaFailed
          ? [manageQuota(canManage && !failed.some((row) => row.details?.canManage === false))]
          : []),
        ...(failed.length
          ? [
              h('details', [
                h('summary', '查看未导入明细'),
                h(Table, {
                  size: 'small',
                  rowKey: 'rowNumber',
                  dataSource: failed,
                  pagination: { pageSize: 10, showSizeChanger: false },
                  columns: [
                    { title: '来源行号', dataIndex: 'rowNumber', width: 100 },
                    { title: '未导入原因', dataIndex: 'message' },
                  ],
                }),
              ]),
            ]
          : []),
      ]),
    });
  }

  async function call(
    method: string,
    payload: Record<string, unknown>,
    request: (input: Record<string, unknown>) => Promise<unknown>,
  ) {
    let canManage = true;
    if (method === 'data.import' && payload.dryRun !== true) {
      const preview = (await request({ ...payload, dryRun: true })) as ComponentDataImportResult;
      canManage = preview.quota?.canManage ?? true;
      if (
        preview.quota &&
        preview.created > preview.quota.remaining &&
        !(await confirmPartial(preview))
      )
        throw {
          code: 'IMPORT_CANCELLED',
          status: 0,
          message: '已取消导入，未写入数据',
          retryable: false,
        } satisfies AppError;
    }
    const result = await request(payload);
    if (method === 'data.import') showResult(result as ComponentDataImportResult, canManage);
    return result;
  }

  function showError(error: AppError) {
    if (error.code !== 'QUOTA_EXCEEDED' || quotaDialog) return;
    const dimension = error.details?.dimension;
    const label =
      dimension === 'recordCount'
        ? '数据库记录'
        : dimension === 'storageBytes'
          ? '存储空间'
          : dimension === 'transferBytes'
            ? '流量'
            : '资源';
    quotaDialog = open({
      title: `${label}额度不足`,
      okText: '知道了',
      content: h('div', [
        h('p', `本次操作未完成：${error.message}。请释放已有用量或扩容后再试。`),
        manageQuota(error.details?.canManage !== false),
      ]),
    });
  }

  function reset() {
    for (const dialog of dialogs) dialog.destroy();
    dialogs.clear();
    batches.clear();
    quotaDialog = undefined;
  }
  onBeforeUnmount(reset);
  return { call, showError, reset };
}
